import { type FC, type ElementType, useMemo, useEffect } from 'react';
import {
  Flex,
  IconButton,
  Stack,
  useDisclosure
} from '@chakra-ui/react';
import {
  UiCard,
  UiText,
  UiIconShoppingCart,
  UiIconList,
  UiHStack,
  UiStack,
  uiStyles,
  UiSpinner,
  UiIconHouse,
  UiDrawer,
  UiDrawerOverlay,
  UiDrawerContent,
  UiDrawerCloseButton,
  UiDrawerBody,
  type UiHStackProps,
} from '@/lib/ui';
import ShoppingCartDrawer from '@/registration/component/Register/ShoppingCart/Drawer';
import { useCartQuery } from '@/registration/hook/useCartQuery';
import { useRegisterRoute } from '@/registration/hook/useRegisterRoute';
import { emptyShoppingCart } from '@/api/registration';
import { useTenantRoute } from '@/account/hook/useTenantRoute';
import { generatePageUrl } from '@/app/pages';
import BaseBlockList from '@/base/BlockList';
import BaseRouterLink from '@/base/Router/Link';
import { topNavHeight } from '@/base/Layout/viewport';
import { useIsEmptyRegisTypeInCart } from '@/registration/hook/useIsEmptyRegisTypeInCart';
import { useNavigate } from 'react-router-dom';
import { useRegisterSteps } from '@/registration/hook/useRegisterSteps';
import { useShoppingCartToggle } from '@/registration/component/Register/ShoppingCart/ShoppingCartToggleProvider';
import LocaleSwitch from '@/registration/component/Locale/LocaleSwitch';

const NO_REDIRECT_URLS = [
  'RegistrationRegisterEmail',
  'RegistrationRegisterCategory',
  'RegistrationRegisterPersonalInfo',
  'RegistrationRegisterMainTickets',
  'RegistrationRegisterInviteeTermsPrivacy',
  'RegistrationRegisterLandingPage',
];

function StoreTopNav() {
  const navigate = useNavigate();
  const { isOpen, onToggle } = useDisclosure();
  // const { isOpen: isShoppingCartOpen, onToggle: onShoppingCartToggle } = useDisclosure();
  const { isOpen: isShoppingCartOpen, onToggle: onShoppingCartToggle } = useShoppingCartToggle();
  const { eventId, tenantCode } = useRegisterRoute();
  const { data: shoppingCart = emptyShoppingCart, isFetching: isShoppingCartFetching } = useCartQuery(eventId);
  const { isEmptyMainTicketInCart } = useIsEmptyRegisTypeInCart(eventId);
  const { currentStep, displayShoppingCart } = useRegisterSteps();
  const isShoppingCartEmpty = useMemo(
    () => {
      return !shoppingCart.cartItems?.length || !Array.isArray(shoppingCart.cartItems) || shoppingCart.cartItems.length === 0;
    },
    [
      shoppingCart.cartItems
    ]
  );

  useEffect(() => {
    if (displayShoppingCart && currentStep?.routeName && isEmptyMainTicketInCart && !NO_REDIRECT_URLS.includes(currentStep?.routeName)) {
      navigate(generatePageUrl('RegistrationRegisterMainTickets', { tenantCode, eventId }));
    }
  }, [currentStep?.routeName, displayShoppingCart, eventId, isEmptyMainTicketInCart, navigate, tenantCode]);

  return (
    <>
      <UiStack
        top={0}
        left={0}
        flexGrow={1}
        bgColor={'transparent'}
      >
        <Flex
          color={'blackAlpha.600'}
          minH={`${topNavHeight}px`}
          maxH={`${topNavHeight}px`}
          py={{ base: 4 }}
          px={{ base: 4, lg: 8 }}
          align={'center'}
        >
          <Flex
            flex={{ base: 1, md: 'auto' }}
          >
            <IconButton
              onClick={onToggle}
              // icon={
              //   isOpen ? <UiIconX size={'3xl'} /> : <UiIconList size={'3xl'} />
              // }
              icon={(
                <UiIconList size={'3xl'} />
              )}
              variant={'ghost'}
              aria-label={'Toggle Navigation'}
            />
          </Flex>
          <Flex flex={{ base: 1 }} justify={{ base: 'center', md: 'start' }} align={'center'} />
          <UiHStack
            flex={{ base: 1, md: 0 }}
            justify={'flex-end'}
            spacing={{base: 4, lg: 8}}
            alignItems={'center'}
          >
            <LocaleSwitch eventId={eventId} tenantCode={tenantCode}/>
            <UiCard
              p={2}
              px={4}
              bgColor={'#fff'}
              enableShadow={false}
            >
              <UiHStack>
                <UiHStack pl={0} onClick={onShoppingCartToggle} {...uiStyles.hover}>
                  {isShoppingCartFetching ? (<UiSpinner size='md' />) : (
                    <>
                      <UiIconShoppingCart
                        size={'3xl'}
                        color={isShoppingCartEmpty ? 'text.primary' : 'primary.500'}
                        weight={isShoppingCartEmpty ? 'regular' : 'regular'}
                      />
                      <UiText
                        variant={isShoppingCartEmpty ? 'body1' : 'title'}
                        color={isShoppingCartEmpty ? 'text.primary' : 'primary.500'}
                      // fontWeight={isShoppingCartEmpty ? 400 : 600}
                      >
                        {shoppingCart.cartItems.length}
                      </UiText>
                    </>
                  )}
                </UiHStack>
              </UiHStack>
            </UiCard>
          </UiHStack>
        </Flex>
      </UiStack>
      <MenuDrawer isOpen={isOpen} onClose={onToggle} />
      <ShoppingCartDrawer isOpen={isShoppingCartOpen} onClose={onShoppingCartToggle} />
    </>
  );
}

interface MenuDrawerProps {
  isOpen: boolean
  onClose: () => void
}

interface MenuItem {
  label: string
  description?: string
  url?: string
  Icon?: ElementType
}

const MenuDrawer = ({
  onClose = () => { },
  isOpen = false
}: MenuDrawerProps) => {
  const { eventId } = useRegisterRoute();
  const { tenant } = useTenantRoute();

  const menuList: MenuItem[] = useMemo(
    () => {
      if (tenant?.code && eventId) {
        return [
          {
            label: 'Event home',
            description: 'Beginning of the registration',
            url: generatePageUrl('RegistrationRegisterEmail', { tenantCode: tenant?.code, eventId }),
            Icon: UiIconHouse,
          }
        ];
      }
      return [];
    },
    [
      tenant?.code,
      eventId,
    ]
  );

  return (
    <UiDrawer
      placement={'left'}
      size={'lg'}
      isOpen={isOpen}
      onClose={onClose}
    >
      <UiDrawerOverlay />
      <UiDrawerContent>
        <UiDrawerCloseButton size={'lg'} color={'primary.500'} />
        <UiDrawerBody px={{ base: 4, lg: 8 }} bgColor={'gray.100'} py={16}>
          <BaseBlockList bgColor={'#fff'}>
            {menuList.map((menuItem, index) => {
              return (
                <MenuDrawerItem key={index} data={menuItem} />
              );
            })}
          </BaseBlockList>
        </UiDrawerBody>
      </UiDrawerContent>
    </UiDrawer>
  );
};

interface MenuDrawerItemProps extends UiHStackProps {
  data: MenuItem
  isActive?: boolean
}

const MenuDrawerItem: FC<MenuDrawerItemProps> = ({
  data,
  isActive = false,
  ...props
}) => {
  // const { isOpen, onToggle } = useDisclosure();
  return (
    <BaseRouterLink to={data.url ?? '#'}>
      <UiHStack spacing={4} {...props} px={{ base: 4, lg: 8 }} py={4}>
        {!!data.Icon && (
          <data.Icon size={'3xl'} />
        )}
        <UiStack spacing={0}>
          <UiText variant={'body1'} fontWeight={600}>{data.label}</UiText>
          {!!data.description && (
            <UiText variant={'body2'} color={'text.secondary'}>{data.description}</UiText>
          )}
        </UiStack>
      </UiHStack>
    </BaseRouterLink>
  );
};

export default StoreTopNav;
